import { useId } from 'react';

/**
 * The accent trend line used by the accuracy widgets.
 *
 * points: array of numbers (e.g. daily accuracy percentages). The line is
 * scaled to fit whatever range the data spans, so it works for 0-100
 * percentages and for raw counts alike.
 *
 * gridLines draws the faint horizontal rules seen on the full Track Record chart.
 */
export function Sparkline({
  points = [],
  height = 80,
  strokeWidth = 2.5,
  gridLines = false,
  className = '',
}) {
  const gradientId = useId();
  const W = 720; // viewBox width; the SVG stretches to its container
  const H = 160;
  const PAD = 10; // keeps the line and end dot off the top/bottom edges

  if (points.length < 2) return null;

  const min = Math.min(...points);
  const max = Math.max(...points);
  // A flat series would divide by zero — draw it down the middle instead.
  const range = max - min || 1;

  const coords = points.map((value, i) => {
    const x = (i / (points.length - 1)) * W;
    const y = PAD + (1 - (value - min) / range) * (H - PAD * 2);
    return [x, y];
  });

  const line = coords.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x},${y}`).join(' ');
  const area = `${line} L${W},${H} L0,${H} Z`;
  const [lastX, lastY] = coords[coords.length - 1];

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      width="100%"
      height={height}
      preserveAspectRatio="none"
      className={className}
      aria-hidden="true"
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="rgba(255,138,20,.32)" />
          <stop offset="1" stopColor="rgba(255,138,20,0)" />
        </linearGradient>
      </defs>

      {gridLines &&
        [0.25, 0.5, 0.75].map((f) => (
          <line
            key={f}
            x1="0"
            y1={H * f}
            x2={W}
            y2={H * f}
            stroke="rgba(255,255,255,.05)"
          />
        ))}

      <path d={area} fill={`url(#${gradientId})`} />
      <path
        d={line}
        fill="none"
        stroke="var(--orange)"
        strokeWidth={strokeWidth}
        vectorEffect="non-scaling-stroke"
        strokeLinejoin="round"
      />
      <circle cx={lastX} cy={lastY} r="5" fill="var(--gold)" />
    </svg>
  );
}
